import type { 
  Company, 
  User, 
  Role, 
  Module, 
  Permission,
  SubscriptionPlan
} from '../types';
import { UserType, Status, PlanType } from '../types';

// Permisos del sistema
export const mockPermissions: Permission[] = [
  // Permisos de Empresas
  {
    id: 'perm-1',
    code: 'companies.view',
    name: 'Ver Empresas',
    description: 'Permite ver el listado de empresas',
    module: 'companies',
    actions: ['read']
  },
  {
    id: 'perm-2',
    code: 'companies.create',
    name: 'Crear Empresas',
    description: 'Permite crear nuevas empresas',
    module: 'companies',
    actions: ['create']
  },
  {
    id: 'perm-3',
    code: 'companies.edit',
    name: 'Editar Empresas',
    description: 'Permite editar empresas existentes',
    module: 'companies',
    actions: ['update']
  },
  {
    id: 'perm-4',
    code: 'companies.delete',
    name: 'Eliminar Empresas',
    description: 'Permite eliminar empresas',
    module: 'companies',
    actions: ['delete']
  },
  // Permisos de Usuarios
  {
    id: 'perm-5',
    code: 'users.view',
    name: 'Ver Usuarios',
    description: 'Permite ver el listado de usuarios',
    module: 'users',
    actions: ['read']
  },
  {
    id: 'perm-6',
    code: 'users.create',
    name: 'Crear Usuarios',
    description: 'Permite crear nuevos usuarios',
    module: 'users',
    actions: ['create']
  },
  {
    id: 'perm-7',
    code: 'users.edit',
    name: 'Editar Usuarios',
    description: 'Permite editar usuarios existentes',
    module: 'users',
    actions: ['update']
  },
  {
    id: 'perm-8',
    code: 'users.delete',
    name: 'Eliminar Usuarios',
    description: 'Permite eliminar usuarios',
    module: 'users',
    actions: ['delete']
  }
];

// Roles del sistema
export const mockRoles: Role[] = [
  {
    id: 'role-1',
    name: 'Super Administrador',
    description: 'Acceso total al sistema',
    companyId: undefined,
    permissions: mockPermissions,
    isSystem: true,
    status: Status.ACTIVE,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  },
  {
    id: 'role-2',
    name: 'Administrador de Empresa',
    description: 'Administrador con acceso total a su empresa',
    companyId: undefined,
    permissions: mockPermissions.filter(p => !p.code.startsWith('companies.')),
    isSystem: true,
    status: Status.ACTIVE,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  },
  {
    id: 'role-3',
    name: 'Gerente',
    description: 'Acceso a módulos de gestión',
    companyId: 'company-1',
    permissions: mockPermissions.filter(p => p.actions.includes('read') || p.actions.includes('update')),
    isSystem: false,
    status: Status.ACTIVE,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  },
  {
    id: 'role-4',
    name: 'Empleado',
    description: 'Acceso básico de lectura',
    companyId: 'company-1',
    permissions: mockPermissions.filter(p => p.actions.includes('read')),
    isSystem: false,
    status: Status.ACTIVE,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  }
];

// Empresas mock
export const mockCompanies: Company[] = [
  {
    id: 'company-1',
    name: 'iTech Solutions S.A. de C.V.',
    tradeName: 'iTech',
    rfc: 'ITS201101ABC',
    email: 'contacto@itech.com.mx',
    phone: '+52 55 1234 5678',
    address: {
      street: 'Av. Reforma',
      number: '123',
      neighborhood: 'Polanco',
      city: 'Ciudad de México',
      state: 'CDMX',
      country: 'México',
      zipCode: '11560'
    },
    logo: 'https://via.placeholder.com/150',
    website: 'https://www.itech.com.mx',
    plan: PlanType.ENTERPRISE,
    maxUsers: 100,
    currentUsers: 45,
    status: Status.ACTIVE,
    settings: {
      timezone: 'America/Mexico_City',
      language: 'es',
      currency: 'MXN',
      fiscalRegime: '601'
    },
    subscription: {
      startDate: new Date('2024-01-01'),
      endDate: new Date('2024-12-31'),
      autoRenew: true,
      paymentMethod: 'credit_card'
    },
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-15'),
    createdBy: 'super-admin'
  },
  {
    id: 'company-2',
    name: 'Comercializadora ABC S.A. de C.V.',
    tradeName: 'ABC Store',
    rfc: 'CAB200505XYZ',
    email: 'admin@abcstore.com',
    phone: '+52 33 9876 5432',
    address: {
      street: 'Av. Patria',
      number: '456',
      neighborhood: 'Jardines del Sol',
      city: 'Guadalajara',
      state: 'Jalisco',
      country: 'México',
      zipCode: '45050'
    },
    website: 'https://www.abcstore.com',
    plan: PlanType.PROFESSIONAL,
    maxUsers: 50,
    currentUsers: 22,
    status: Status.ACTIVE,
    settings: {
      timezone: 'America/Mexico_City',
      language: 'es',
      currency: 'MXN',
      fiscalRegime: '603'
    },
    subscription: {
      startDate: new Date('2024-02-01'),
      endDate: new Date('2025-01-31'),
      autoRenew: true,
      paymentMethod: 'bank_transfer'
    },
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01'),
    createdBy: 'super-admin'
  },
  {
    id: 'company-3',
    name: 'Startup Digital S.A.P.I.',
    tradeName: 'StartupMX',
    rfc: 'SDI210815QWE',
    email: 'hola@startupmx.io',
    phone: '+52 81 2345 6789',
    address: {
      street: 'Av. Constitución',
      number: '789',
      neighborhood: 'Centro',
      city: 'Monterrey',
      state: 'Nuevo León',
      country: 'México',
      zipCode: '64000'
    },
    website: 'https://startupmx.io',
    plan: PlanType.BASIC,
    maxUsers: 10,
    currentUsers: 5,
    status: Status.ACTIVE,
    settings: {
      timezone: 'America/Mexico_City',
      language: 'es',
      currency: 'MXN',
      fiscalRegime: '626'
    },
    subscription: {
      startDate: new Date('2024-03-01'),
      endDate: new Date('2025-02-28'),
      autoRenew: false,
      paymentMethod: 'paypal'
    },
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-01'),
    createdBy: 'super-admin'
  }
];

// Usuarios mock
export const mockUsers: User[] = [
  // Super Admin
  {
    id: 'user-super',
    email: 'super@admin.com',
    username: 'superadmin',
    type: UserType.SUPER_ADMIN,
    companyId: undefined,
    profile: {
      firstName: 'Super',
      lastName: 'Admin',
      phone: '+52 55 0000 0000',
      avatar: 'https://ui-avatars.com/api/?name=Super+Admin&background=556ee6&color=fff',
      preferences: {
        language: 'es',
        timezone: 'America/Mexico_City',
        notifications: {
          email: true,
          sms: true,
          push: true
        },
        theme: 'light'
      }
    },
    roles: [mockRoles[0]], // Super Admin role
    status: Status.ACTIVE,
    emailVerified: true,
    twoFactorEnabled: true,
    lastLogin: new Date('2024-08-13'),
    loginAttempts: 0,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  },
  // Admin de iTech
  {
    id: 'user-1',
    email: 'admin@itech.com.mx',
    username: 'admin_itech',
    type: UserType.COMPANY_ADMIN,
    companyId: 'company-1',
    profile: {
      firstName: 'Carlos',
      lastName: 'Rodríguez',
      phone: '+52 55 1234 5679',
      avatar: 'https://ui-avatars.com/api/?name=Carlos+Rodriguez&background=34c38f&color=fff',
      department: 'Dirección',
      position: 'Director General',
      employeeId: 'EMP001',
      preferences: {
        language: 'es',
        timezone: 'America/Mexico_City',
        notifications: {
          email: true,
          sms: false,
          push: true
        },
        theme: 'light'
      }
    },
    roles: [mockRoles[1]], // Company Admin role
    status: Status.ACTIVE,
    emailVerified: true,
    twoFactorEnabled: false,
    lastLogin: new Date('2024-08-12'),
    loginAttempts: 0,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'super-admin'
  },
  // Gerente de iTech
  {
    id: 'user-2',
    email: 'gerente@itech.com.mx',
    username: 'gerente_itech',
    type: UserType.COMPANY_USER,
    companyId: 'company-1',
    profile: {
      firstName: 'María',
      lastName: 'González',
      phone: '+52 55 1234 5680',
      avatar: 'https://ui-avatars.com/api/?name=Maria+Gonzalez&background=f1b44c&color=fff',
      department: 'Ventas',
      position: 'Gerente de Ventas',
      employeeId: 'EMP002',
      preferences: {
        language: 'es',
        timezone: 'America/Mexico_City',
        notifications: {
          email: true,
          sms: false,
          push: false
        },
        theme: 'light'
      }
    },
    roles: [mockRoles[2]], // Gerente role
    status: Status.ACTIVE,
    emailVerified: true,
    twoFactorEnabled: false,
    lastLogin: new Date('2024-08-10'),
    loginAttempts: 0,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
    createdBy: 'user-1'
  },
  // Empleado de iTech
  {
    id: 'user-3',
    email: 'empleado@itech.com.mx',
    username: 'empleado_itech',
    type: UserType.COMPANY_USER,
    companyId: 'company-1',
    profile: {
      firstName: 'Juan',
      lastName: 'Pérez',
      phone: '+52 55 1234 5681',
      avatar: 'https://ui-avatars.com/api/?name=Juan+Perez&background=50a5f1&color=fff',
      department: 'Contabilidad',
      position: 'Contador',
      employeeId: 'EMP003',
      preferences: {
        language: 'es',
        timezone: 'America/Mexico_City',
        notifications: {
          email: true,
          sms: false,
          push: false
        },
        theme: 'dark'
      }
    },
    roles: [mockRoles[3]], // Empleado role
    status: Status.ACTIVE,
    emailVerified: true,
    twoFactorEnabled: false,
    lastLogin: new Date('2024-08-11'),
    loginAttempts: 0,
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01'),
    createdBy: 'user-1'
  },
  // Admin de ABC Store
  {
    id: 'user-4',
    email: 'admin@abcstore.com',
    username: 'admin_abc',
    type: UserType.COMPANY_ADMIN,
    companyId: 'company-2',
    profile: {
      firstName: 'Ana',
      lastName: 'Martínez',
      phone: '+52 33 9876 5433',
      avatar: 'https://ui-avatars.com/api/?name=Ana+Martinez&background=f46a6a&color=fff',
      department: 'Dirección',
      position: 'Directora General',
      employeeId: 'ABC001',
      preferences: {
        language: 'es',
        timezone: 'America/Mexico_City',
        notifications: {
          email: true,
          sms: true,
          push: true
        },
        theme: 'light'
      }
    },
    roles: [mockRoles[1]], // Company Admin role
    status: Status.ACTIVE,
    emailVerified: true,
    twoFactorEnabled: true,
    lastLogin: new Date('2024-08-13'),
    loginAttempts: 0,
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01'),
    createdBy: 'super-admin'
  }
];

// Módulos del sistema
export const mockModules: Module[] = [
  {
    id: 'mod-1',
    code: 'dashboard',
    name: 'Dashboard',
    description: 'Panel de control principal',
    icon: 'Home',
    route: '/dashboard',
    order: 1,
    permissions: [],
    requiredPlan: [PlanType.FREE, PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-2',
    code: 'companies',
    name: 'Empresas',
    description: 'Gestión de empresas',
    icon: 'Building',
    route: '/admin/companies',
    order: 2,
    permissions: mockPermissions.filter(p => p.module === 'companies'),
    requiredPlan: [PlanType.ENTERPRISE], // Solo para super admin
    isActive: true
  },
  {
    id: 'mod-3',
    code: 'users',
    name: 'Usuarios',
    description: 'Gestión de usuarios',
    icon: 'Users',
    route: '/users',
    order: 3,
    permissions: mockPermissions.filter(p => p.module === 'users'),
    requiredPlan: [PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-4',
    code: 'roles',
    name: 'Roles y Permisos',
    description: 'Gestión de roles y permisos',
    icon: 'Shield',
    route: '/roles',
    order: 4,
    permissions: [],
    requiredPlan: [PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-5',
    code: 'products',
    name: 'Productos',
    description: 'Catálogo de productos',
    icon: 'Package',
    route: '/products',
    order: 5,
    permissions: [],
    requiredPlan: [PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-6',
    code: 'sales',
    name: 'Ventas',
    description: 'Gestión de ventas',
    icon: 'ShoppingCart',
    route: '/sales',
    order: 6,
    permissions: [],
    requiredPlan: [PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-7',
    code: 'reports',
    name: 'Reportes',
    description: 'Reportes y análisis',
    icon: 'BarChart',
    route: '/reports',
    order: 7,
    permissions: [],
    requiredPlan: [PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-8',
    code: 'phone-numbers',
    name: 'Números Telefónicos',
    description: 'Gestión de números telefónicos',
    icon: 'Phone',
    route: '/phone-numbers',
    order: 8,
    permissions: [],
    requiredPlan: [PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-9',
    code: 'assistants',
    name: 'Asistentes IA',
    description: 'Gestión de asistentes virtuales',
    icon: 'Bot',
    route: '/assistants',
    order: 9,
    permissions: [],
    requiredPlan: [PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-10',
    code: 'automations',
    name: 'Automatizaciones',
    description: 'Flujos de trabajo automatizados',
    icon: 'GitBranch',
    route: '/automations',
    order: 10,
    permissions: [],
    requiredPlan: [PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-11',
    code: 'whatsapp',
    name: 'WhatsApp Business',
    description: 'Gestión de conversaciones WhatsApp',
    icon: 'MessageSquare',
    route: '/whatsapp',
    order: 11,
    permissions: [],
    requiredPlan: [PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-12',
    code: 'setup-wizard',
    name: 'Configuración Inicial',
    description: 'Asistente de configuración',
    icon: 'Zap',
    route: '/setup-wizard',
    order: 12,
    permissions: [],
    requiredPlan: [PlanType.FREE, PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  },
  {
    id: 'mod-13',
    code: 'settings',
    name: 'Configuración',
    description: 'Configuración del sistema',
    icon: 'Settings',
    route: '/settings',
    order: 13,
    permissions: [],
    requiredPlan: [PlanType.FREE, PlanType.BASIC, PlanType.PROFESSIONAL, PlanType.ENTERPRISE],
    isActive: true
  }
];

// Planes de suscripción
export const mockSubscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'plan-1',
    name: 'Gratis',
    code: PlanType.FREE,
    description: 'Perfecto para empezar',
    features: [
      '1 usuario',
      'Dashboard básico',
      'Soporte por email'
    ],
    price: {
      monthly: 0,
      yearly: 0,
      currency: 'MXN'
    },
    limits: {
      users: 1,
      storage: 1,
      apiCalls: 1000,
      customRoles: 0,
      branches: 1
    },
    modules: ['dashboard', 'settings'],
    isPopular: false,
    isActive: true
  },
  {
    id: 'plan-2',
    name: 'Básico',
    code: PlanType.BASIC,
    description: 'Para pequeños negocios',
    features: [
      'Hasta 10 usuarios',
      'Productos y ventas',
      'Soporte prioritario',
      '5 GB de almacenamiento'
    ],
    price: {
      monthly: 499,
      yearly: 4990,
      currency: 'MXN'
    },
    limits: {
      users: 10,
      storage: 5,
      apiCalls: 10000,
      customRoles: 3,
      branches: 2
    },
    modules: ['dashboard', 'users', 'products', 'sales', 'settings'],
    isPopular: false,
    isActive: true
  },
  {
    id: 'plan-3',
    name: 'Profesional',
    code: PlanType.PROFESSIONAL,
    description: 'Para empresas en crecimiento',
    features: [
      'Hasta 50 usuarios',
      'Todos los módulos',
      'Reportes avanzados',
      'Roles personalizados',
      '50 GB de almacenamiento',
      'API access'
    ],
    price: {
      monthly: 1499,
      yearly: 14990,
      currency: 'MXN'
    },
    limits: {
      users: 50,
      storage: 50,
      apiCalls: 100000,
      customRoles: 10,
      branches: 5
    },
    modules: ['dashboard', 'users', 'roles', 'products', 'sales', 'reports', 'settings'],
    isPopular: true,
    isActive: true
  },
  {
    id: 'plan-4',
    name: 'Empresarial',
    code: PlanType.ENTERPRISE,
    description: 'Solución completa',
    features: [
      'Usuarios ilimitados',
      'Todos los módulos',
      'Soporte 24/7',
      'Personalización completa',
      'Almacenamiento ilimitado',
      'API ilimitada',
      'Multi-sucursal'
    ],
    price: {
      monthly: 4999,
      yearly: 49990,
      currency: 'MXN'
    },
    limits: {
      users: 9999,
      storage: 9999,
      apiCalls: 999999,
      customRoles: 999,
      branches: 999
    },
    modules: ['dashboard', 'companies', 'users', 'roles', 'products', 'sales', 'reports', 'settings'],
    isPopular: false,
    isActive: true
  }
];